import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@repo/common/utils/cn";
import { _disabledCursor, _disabledPointer, _focus } from "./shared-styles";
import { textBodyVariants } from "@/components/ui/text";
const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;
const _triggerBase = "flex h-9 w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2";
const _triggerTypography = `${textBodyVariants()} data-[placeholder]:text-gray-500 [&>span]:line-clamp-1 text-left`;
const _triggerRest = "[&>span]:line-clamp-1";
const SelectTrigger = t0 => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn([_triggerBase, _triggerTypography, _focus, _disabledCursor], className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectPrimitive.Icon asChild={true}><ChevronDown className="h-4 w-4 opacity-50" /></SelectPrimitive.Icon>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== t1) {
    t3 = <SelectPrimitive.Trigger data-slot="select-trigger" className={t1} {...props}>{children}{t2}</SelectPrimitive.Trigger>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

/**
 *
 * Select Scroll Buttons
 *
 */

const _selectScrollButton = "flex cursor-default items-center justify-center py-1";
const SelectScrollUpButton = t0 => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn([_selectScrollButton], className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronUp className="h-4 w-4" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollUpButton data-slot="select-scroll-up-button" className={t1} {...props}>{t2}</SelectPrimitive.ScrollUpButton>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
const SelectScrollDownButton = t0 => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn([_selectScrollButton], className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronDown className="h-4 w-4" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollDownButton data-slot="select-scroll-down-button" className={t1} {...props}>{t2}</SelectPrimitive.ScrollDownButton>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

/**
 *
 * Select Content
 *
 */

const _contentBase = "relative z-50 overflow-hidden rounded-md border text-base bg-background text-foreground shadow-md";
const _contentSize = "max-h-96 min-w-[8rem]";
const _contentOpen = `
  data-[state=open]:animate-in
  data-[state=closed]:animate-out 
  data-[state=closed]:fade-out-0
  data-[state=open]:fade-in-0
  data-[state=closed]:zoom-out-95
  data-[state=open]:zoom-in-95
`;
const _contentPosition = `
  data-[side=bottom]:slide-in-from-top-2
  data-[side=left]:slide-in-from-right-2
  data-[side=right]:slide-in-from-left-2
  data-[side=top]:slide-in-from-bottom-2
`;
const _contentPopper = `
  data-[side=bottom]:translate-y-1 
  data-[side=left]:-translate-x-1 
  data-[side=right]:translate-x-1 
  data-[side=top]:-translate-y-1
`;
const _viewport = "p-1 flex flex-col space-y-0.5";
// adjust the max width to remove the border width
const _viewportPopper = `
  h-[var(--radix-select-trigger-height)]
  w-full
  min-w-[calc(var(--radix-select-trigger-width)-2px)]
`;
const SelectContent = t0 => {
  const $ = _c(20);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      position: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const position = t1 === undefined ? "popper" : t1;
  let t2;
  if ($[5] !== className || $[6] !== position) {
    t2 = cn([_contentBase, _contentSize, _contentOpen, _contentPosition], position === "popper" && [_contentPopper], className);
    $[5] = className;
    $[6] = position;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectScrollUpButton />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = position === "popper" && _viewportPopper;
  let t5;
  if ($[9] !== t4) {
    t5 = cn(_viewport, t4);
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== children || $[12] !== t5) {
    t6 = <SelectPrimitive.Viewport className={t5}>{children}</SelectPrimitive.Viewport>;
    $[11] = children;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SelectScrollDownButton />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== position || $[16] !== props || $[17] !== t2 || $[18] !== t6) {
    t8 = <SelectPrimitive.Portal><SelectPrimitive.Content data-slot="select-content" className={t2} position={position} {...props}>{t3}{t6}{t7}</SelectPrimitive.Content></SelectPrimitive.Portal>;
    $[15] = position;
    $[16] = props;
    $[17] = t2;
    $[18] = t6;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
SelectContent.displayName = SelectPrimitive.Content.displayName;

/**
 *
 * Select Label
 *
 */

const SelectLabel = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Label data-slot="select-label" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
SelectLabel.displayName = SelectPrimitive.Label.displayName;

/**
 *
 * Select Item
 *
 */

// Item styles
const _itemBase = "relative flex w-full cursor-default select-none items-center justify-between rounded-sm py-1.5 px-2";
const _itemTypography = textBodyVariants();
// review aginst focus shared; focus is how hover styles work?
const _itemFocus = "outline-none focus:bg-gray-50 focus:cursor-pointer";
const _sharedFocus = "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-blue-600 focus-visible:ring-offset-2";
// const _itemDisabled =
//   "data-[disabled]:pointer-events-none data-[disabled]:opacity-50";

// Indicator
const _indicator = "h-5 w-5 flex items-center justify-center";
const SelectItem = t0 => {
  const $ = _c(20);
  let children;
  let className;
  let endEnhancer;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      endEnhancer,
      showIndicator: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = endEnhancer;
    $[4] = props;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    endEnhancer = $[3];
    props = $[4];
    t1 = $[5];
  }
  const showIndicator = t1 === undefined ? true : t1;
  let t2;
  if ($[6] !== className) {
    t2 = cn([_itemBase, _itemTypography, _itemFocus, _disabledPointer], className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children) {
    t3 = <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>;
    $[8] = children;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== showIndicator) {
    t4 = showIndicator && <SelectPrimitive.ItemIndicator><Check className="h-5 w-5" /></SelectPrimitive.ItemIndicator>;
    $[10] = showIndicator;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== endEnhancer || $[13] !== t4) {
    t5 = <span className={_indicator}>{endEnhancer}{t4}</span>;
    $[12] = endEnhancer;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== props || $[16] !== t2 || $[17] !== t3 || $[18] !== t5) {
    t6 = <SelectPrimitive.Item data-slot="select-item" className={t2} {...props}>{t3}{t5}</SelectPrimitive.Item>;
    $[15] = props;
    $[16] = t2;
    $[17] = t3;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
SelectItem.displayName = SelectPrimitive.Item.displayName;
const SelectSeparator = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-background-muted", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Separator data-slot="select-separator" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
export { Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectLabel, SelectItem, SelectSeparator, SelectScrollUpButton, SelectScrollDownButton };